"use client";

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { cn } from "@/shared/lib/utils";
import type { HomeFaqSectionContent } from "@/features/home/types/home-page-content";

interface HomeFaqSectionProps {
  content: HomeFaqSectionContent;
}

export function HomeFaqSection({ content }: HomeFaqSectionProps) {
  const [openId, setOpenId] = useState<string | null>(content.items[0]?.id ?? null);

  const toggleAccordion = (id: string) => {
    setOpenId((prev) => (prev === id ? null : id));
  };

  return (
    <section id={content.id} className="relative w-full bg-background py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="relative flex flex-col items-start gap-16 lg:flex-row lg:gap-24">
          {/* Left Sticky Content */}
          <div className="static z-20 h-fit lg:sticky lg:top-40 lg:w-[40%]">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.7, ease: "easeOut" }}
            >
              <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-4 py-1.5 shadow-sm">
                <span className="flex h-1.5 w-1.5 rounded-full bg-primary" />
                <span className="text-xs font-semibold tracking-wider text-primary uppercase">
                  {content.eyebrow}
                </span>
              </div>

              <h2 className="text-4xl leading-[1.1] font-semibold tracking-tight text-balance text-foreground sm:text-5xl">
                {content.title}
              </h2>

              <p className="mt-6 hidden max-w-sm text-lg leading-relaxed text-muted-foreground sm:block">
                {content.description}
              </p>
            </motion.div>
          </div>

          {/* Right Scrollable Content List */}
          <div className="z-10 flex w-full flex-col gap-4 lg:w-[60%]">
            {content.items.map((faq, index) => {
              const isOpen = openId === faq.id;

              return (
                <motion.div
                  key={faq.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className={cn(
                    "flex flex-col overflow-hidden rounded-3xl border transition-colors duration-300",
                    isOpen
                      ? "border-blue-100 bg-white shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
                      : "border-transparent bg-transparent hover:bg-white/50",
                  )}
                >
                  <button
                    onClick={() => toggleAccordion(faq.id)}
                    className="flex w-full items-start justify-between p-6 text-left outline-none sm:p-8"
                  >
                    <div className="relative top-[-4px] flex flex-col gap-2">
                      <span className="text-xs font-semibold text-slate-400">{faq.id}</span>
                      <h3
                        className={cn(
                          "text-xl font-semibold tracking-tight transition-colors duration-300 sm:text-[22px]",
                          isOpen ? "text-primary" : "text-foreground",
                        )}
                      >
                        {faq.question}
                      </h3>
                    </div>

                    <div
                      className={cn(
                        "ml-4 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-2xl transition-all duration-300 sm:h-12 sm:w-12",
                        isOpen
                          ? "bg-primary shadow-md"
                          : "hidden bg-muted hover:bg-muted-foreground/10 sm:flex",
                      )}
                    >
                      <motion.div
                        animate={{ rotate: isOpen ? 180 : 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                      >
                        <ChevronDown
                          className={cn(
                            "h-5 w-5",
                            isOpen ? "text-primary-foreground" : "text-foreground",
                          )}
                          strokeWidth={2.5}
                        />
                      </motion.div>
                    </div>

                    {/* Mobile Chevron */}
                    <div className="ml-4 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-muted sm:hidden">
                      <motion.div
                        animate={{ rotate: isOpen ? 180 : 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        <ChevronDown className="h-4 w-4 text-foreground" />
                      </motion.div>
                    </div>
                  </button>

                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.4, ease: [0.04, 0.62, 0.23, 0.98] }}
                      >
                        <div className="flex flex-col gap-6 px-6 pt-0 pb-8 sm:px-8 sm:pb-8">
                          <div className="mt-[-10px] mb-2 h-px w-full bg-border"></div>
                          <p className="max-w-3xl text-[15px] leading-relaxed text-muted-foreground sm:text-base">
                            {faq.answer}
                          </p>
                          {faq.tags && faq.tags.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-3">
                              {faq.tags.map((tag) => (
                                <span
                                  key={tag}
                                  className="inline-flex items-center rounded-full border border-primary/20 bg-primary/10 px-4 py-1.5 text-[13px] font-semibold text-primary"
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
